const BASE = "/api/v1";

let accessToken: string | null = null;
const tokenListeners = new Set<(t: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenListeners.forEach((fn) => fn(token));
}
export const getAccessToken = (): string | null => accessToken;
export function onToken(fn: (t: string | null) => void): () => void {
  tokenListeners.add(fn);
  return () => tokenListeners.delete(fn);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Refresh relies on the `brvm_rt` httpOnly cookie the API sets on login/
 * refresh (path `/api/v1/auth`) — never on a token kept in JS-reachable
 * storage. This is the deliberate difference from the admin SPA, which
 * persists the refresh token to localStorage; the end-user client has a
 * much larger XSS surface (public opportunity content, rich text) so the
 * refresh token itself must stay out of reach of any injected script.
 */
export async function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  })
    .then(async (res) => {
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

interface Options extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { body, headers, ...rest } = opts;
  const build = (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res = await build();
  if (res.status === 401 && !path.startsWith("/auth/")) {
    if (await tryRefresh()) res = await build();
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } })?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "error",
      err?.message ?? `HTTP ${res.status}`,
      err?.details,
    );
  }
  return data as T;
}
